<template>
  <div class="rm-screen">
    <div v-if="!selectedCompanies.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
      {{ $t("no_companies_selected") }}
    </div>
    <div v-else-if="fetching" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!takenCompanies.length" class="rm-empty">
      {{
        filters.search
          ? $t("report_mobile.no_results_for", { search: filters.search })
          : $t("no_taken_meals_data")
      }}
    </div>

    <template v-else>
      <div class="rm-summary">
        <div class="rm-summary__row">
          <div class="rm-summary__labels">
            <span>{{ $t("t_o") }}</span>
            <span>
              {{ pluralT("report_mobile.companies_n", takenCompanies.length) }} ·
              {{ pluralT("report_mobile.meals_n", summary.ordered) }}
            </span>
          </div>
          <span class="rm-summary__value">
            {{ summary.taken }}<span class="rm-summary__value-of"> / {{ summary.ordered }}</span>
          </span>
        </div>
        <div
          class="rm-progress rm-progress--inverse"
          role="progressbar"
          :aria-valuenow="summary.taken"
          aria-valuemin="0"
          :aria-valuemax="summary.ordered"
        >
          <div class="rm-progress__fill" :style="{ width: `${summary.percent}%` }"></div>
        </div>
      </div>

      <section v-for="company in takenCompanies" :key="company.key" class="rm-card rm-mcard">
        <button
          type="button"
          class="rm-mcard__head"
          :aria-expanded="String(isOpen(company.key))"
          @click="toggle(company.key)"
        >
          <span class="rm-mcard__title">
            <span class="rm-mcard__name">{{ company.name }}</span>
            <span class="rm-muted">{{ pluralT("report_mobile.meals_n", company.ordered) }}</span>
          </span>
          <span class="rm-mcard__value rm-num">
            {{ company.taken }}<span class="rm-mcard__of"> / {{ company.ordered }}</span>
          </span>
          <v-icon size="18" color="#8a817a" class="rm-mcard__chevron" :class="{ 'is-open': isOpen(company.key) }">
            mdi-chevron-down
          </v-icon>
        </button>
        <div
          class="rm-progress rm-mcard__progress"
          :class="{ 'rm-progress--complete': company.complete }"
          role="progressbar"
          :aria-valuenow="company.taken"
          aria-valuemin="0"
          :aria-valuemax="company.ordered"
        >
          <div class="rm-progress__fill" :style="{ width: `${company.percent}%` }"></div>
        </div>

        <ul v-if="isOpen(company.key)" class="rm-list rm-mcard__list">
          <li v-for="meal in company.meals" :key="meal.key" class="rm-mrow">
            <div class="rm-mrow__main">
              <span class="rm-mrow__name">{{ meal.name }}</span>
              <span class="rm-mrow__value rm-num" :class="{ 'is-complete': meal.complete }">
                <v-icon v-if="meal.complete" size="16" color="#15803d">mdi-check-circle</v-icon>
                {{ meal.taken }}<span class="rm-mrow__of"> / {{ meal.total }}</span>
              </span>
            </div>
            <div v-if="meal.shifts.length" class="rm-mrow__shifts">
              <span v-for="shift in meal.shifts" :key="shift.id" class="rm-mrow__shift rm-num">
                <report-shift-tag :shift="shift.id"></report-shift-tag>
                {{ shift.taken }} / {{ shift.orders }}
              </span>
            </div>
          </li>
          <li v-if="!company.meals.length" class="rm-mrow rm-muted">
            {{ $t("report_mobile.no_results_for", { search: filters.search }) }}
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import ReportMealsTabTakenMeals from "@/components/report/meals/tabs/ReportMealsTabTakenMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import hasBarCompanies from "../../shared/hasBarCompanies";
import hasBarShifts from "../../shared/hasBarShifts";
import hasPlural from "../../shared/hasPlural";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";
import { percentOf } from "../../shared/takenMath";
import "./reportMobileMealCards.scss";

/**
 * Mobilni prikaz "Preuzeti obroci": po firmi preuzeto / poručeno, a po obroku i po smeni. Firme, smene,
 * period i pretraga su u zaglavlju (kao u ostalim izveštajima). Nasleđuje učitavanje, štampu i Excel iz
 * ReportMealsTabTakenMeals (štampa i Excel prate izabrane firme i smene).
 */
export default {
  name: "ReportMobileTakenMeals",
  extends: ReportMealsTabTakenMeals,
  mixins: [hasBarCompanies, hasBarShifts, hasPlural],
  components: { ReportShiftTag },
  data: () => ({
    fetching: true,
    closed: {},
  }),
  computed: {
    /** Firme sa obrocima: obroci po smeni (samo smene sa narudžbama) i zbir firme. */
    takenCompanies() {
      const shifts = SHIFTS.filter((shift) => this.selectedShifts.includes(shift.id));
      const search = this.filters.search;
      return this.companies
        .map((company) => {
          const found = search ? this.customSearch(company.meals, [{ value: "name" }], search) : company.meals;
          const meals = found.map((meal) => ({
            key: `${company.id || company.name}-${meal.id || meal.num}`,
            name: this.toLatin(meal.name),
            taken: meal.totalPayed || 0,
            total: meal.total || 0,
            complete: meal.total > 0 && meal.totalPayed >= meal.total,
            shifts: shifts
              .map((shift) => ({
                id: shift.id,
                taken: meal[shift.key]?.taken || 0,
                orders: meal[shift.key]?.orders || 0,
              }))
              .filter((shift) => shift.orders > 0),
          }));
          const totals = company.totals || {};
          const ordered = totals.total || 0;
          const taken = totals.totalPayed || 0;
          return {
            key: String(company.id || company.name),
            name: this.toLatin(company.name),
            taken,
            ordered,
            complete: ordered > 0 && taken >= ordered,
            percent: percentOf(taken, ordered),
            meals,
          };
        })
        .filter((company) => company.ordered > 0 || company.taken > 0)
        .filter((company) => !search || company.meals.length);
    },
    summary() {
      const taken = this.takenCompanies.reduce((sum, company) => sum + company.taken, 0);
      const ordered = this.takenCompanies.reduce((sum, company) => sum + company.ordered, 0);
      return { taken, ordered, percent: percentOf(taken, ordered) };
    },
  },
  methods: {
    isOpen(key) {
      return Boolean(this.filters.search) || !this.closed[key];
    },
    toggle(key) {
      this.closed = { ...this.closed, [key]: !this.closed[key] };
    },
    /** Isto učitavanje kao u starom tabu, uz stanje "učitava" (stari tab ga ne razlikuje od praznog izveštaja). */
    async loadMealsForRestaurantCompanies() {
      if (!this.selectedCompanies.length) {
        this.report = [];
        this.fetching = false;
        return;
      }
      this.fetching = true;
      try {
        await ReportMealsTabTakenMeals.methods.loadMealsForRestaurantCompanies.call(this);
      } finally {
        this.fetching = false;
      }
    },
  },
};
</script>
